import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import GestorLayout from '../../components/GestorLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Sparkles, KeyRound, Loader2, CheckCircle2, XCircle, ExternalLink, Save, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Navigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminOpenAI() {
  const { user, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [config, setConfig] = useState(null);
  const [models, setModels] = useState([]);
  const [defaultModel, setDefaultModel] = useState('gpt-4o-mini');
  const [apiKey, setApiKey] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [model, setModel] = useState('gpt-4o-mini');
  const [testResult, setTestResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cfgRes, modelsRes] = await Promise.all([
        axios.get(`${API}/admin/openai`),
        axios.get(`${API}/admin/openai/models`),
      ]);
      setConfig(cfgRes.data);
      setModels(modelsRes.data?.models || []);
      setDefaultModel(modelsRes.data?.default || 'gpt-4o-mini');
      setModel(cfgRes.data?.model || modelsRes.data?.default || 'gpt-4o-mini');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro a carregar configuração OpenAI');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Guard: apenas admin (o backend também rejeita, mas evitamos flash de UI)
  if (!authLoading && user && user.role !== 'admin' && user.perfil !== 'admin') {
    return <Navigate to="/gestor/dashboard" replace />;
  }

  const guardar = async () => {
    const payload = { model };
    if (apiKey.trim() !== '') payload.api_key = apiKey.trim();
    setSaving(true);
    setTestResult(null);
    try {
      const res = await axios.put(`${API}/admin/openai`, payload);
      setConfig(res.data);
      setApiKey(''); // Limpar campo por segurança
      toast.success('Configuração OpenAI guardada.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro a guardar configuração.');
    } finally {
      setSaving(false);
    }
  };

  const limparChave = async () => {
    if (!window.confirm('Remover a chave OpenAI guardada? A sugestão de mensagem por IA deixará de funcionar até adicionares uma nova chave.')) return;
    setSaving(true);
    try {
      await axios.put(`${API}/admin/openai`, { api_key: '' });
      toast.success('Chave removida.');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro a remover chave.');
    } finally {
      setSaving(false);
    }
  };

  const testar = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const payload = { model };
      if (apiKey.trim() !== '') payload.api_key = apiKey.trim();
      const res = await axios.post(`${API}/admin/openai/test`, payload);
      setTestResult(res.data);
      if (res.data?.ok) {
        toast.success(`Chave válida — modelo ${res.data.model} respondeu.`);
      } else {
        toast.error(res.data?.error || 'Teste falhou.');
      }
    } catch (e) {
      const detail = e.response?.data?.detail || 'Falha ao testar.';
      setTestResult({ ok: false, error: detail });
      toast.error(detail);
    } finally {
      setTesting(false);
    }
  };

  return (
    <GestorLayout>
      <div className="max-w-3xl mx-auto p-4 md:p-6 space-y-4" data-testid="admin-openai-page">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
            <Sparkles className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-navy">OpenAI / ChatGPT</h1>
            <p className="text-sm text-stone-500">Configura a chave e o modelo que geram as sugestões de mensagem à equipa no Ranking Final.</p>
          </div>
        </div>

        <Card className="p-5 space-y-5" data-testid="admin-openai-card">
          {/* Estado actual */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="text-xs font-semibold text-stone-500 uppercase tracking-wide">Estado</div>
            {loading ? (
              <span className="text-xs text-stone-400 flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> A carregar…</span>
            ) : config?.configured ? (
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-1 rounded-full" data-testid="admin-openai-status-configured">
                <CheckCircle2 className="h-3.5 w-3.5" /> Chave configurada
                <span className="text-stone-500 font-normal ml-1">({config.key_preview})</span>
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-800 bg-amber-50 border border-amber-200 px-2 py-1 rounded-full" data-testid="admin-openai-status-empty">
                <XCircle className="h-3.5 w-3.5" /> Sem chave configurada
              </span>
            )}
            {config?.updated_at && (
              <span className="text-[11px] text-stone-400">actualizado: {new Date(config.updated_at).toLocaleString('pt-PT')}</span>
            )}
          </div>

          {/* Chave */}
          <div>
            <Label className="text-xs flex items-center gap-1"><KeyRound className="h-3.5 w-3.5" /> Chave OpenAI (secret key)</Label>
            <div className="flex gap-2 mt-1">
              <div className="relative flex-1">
                <Input
                  type={showKey ? 'text' : 'password'}
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder={config?.configured ? `••••••••••  (guardada: ${config.key_preview}) — deixa vazio para manter` : 'sk-...'}
                  className="pr-10 font-mono text-sm"
                  autoComplete="off"
                  spellCheck={false}
                  data-testid="admin-openai-key-input"
                />
                <button
                  type="button"
                  onClick={() => setShowKey((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-700"
                  aria-label="Mostrar/Esconder"
                >
                  {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <p className="text-[11px] text-stone-500 mt-1 flex items-center gap-1">
              Obtém uma chave em
              <a href="https://platform.openai.com/api-keys" target="_blank" rel="noreferrer" className="text-blue-600 hover:underline inline-flex items-center gap-0.5">
                platform.openai.com/api-keys <ExternalLink className="h-3 w-3" />
              </a>
              . A chave nunca é devolvida completa pela API — só o preview dos últimos 4 caracteres.
            </p>
          </div>

          {/* Modelo */}
          <div>
            <Label className="text-xs">Modelo</Label>
            <select
              value={model}
              onChange={(e) => setModel(e.target.value)}
              className="h-9 w-full border rounded-md px-2 text-sm bg-white mt-1"
              data-testid="admin-openai-model-select"
            >
              {(models.length ? models : [{ id: defaultModel, label: defaultModel }]).map((m) => (
                <option key={m.id} value={m.id}>{m.label}</option>
              ))}
            </select>
            <p className="text-[11px] text-stone-500 mt-1">A tua conta OpenAI precisa de acesso ao modelo escolhido. Se falhar, o botão &quot;Testar&quot; indica.</p>
          </div>

          {/* Resultado do teste */}
          {testResult && (
            <div
              className={`text-sm px-3 py-2 rounded-lg border ${testResult.ok ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-red-50 border-red-200 text-red-800'}`}
              data-testid="admin-openai-test-result"
            >
              {testResult.ok ? (
                <>
                  <div className="flex items-center gap-1.5 font-semibold"><CheckCircle2 className="h-4 w-4" /> Teste OK</div>
                  <div className="text-xs mt-1">Modelo <code className="bg-white/60 px-1 rounded">{testResult.model}</code> respondeu: <em>“{testResult.reply}”</em></div>
                </>
              ) : (
                <>
                  <div className="flex items-center gap-1.5 font-semibold"><XCircle className="h-4 w-4" /> Teste falhou</div>
                  <div className="text-xs mt-1">{testResult.error}</div>
                </>
              )}
            </div>
          )}

          {/* Acções */}
          <div className="flex flex-wrap gap-2 justify-between pt-2">
            <div>
              {config?.configured && (
                <Button type="button" variant="outline" size="sm" onClick={limparChave} disabled={saving} className="text-red-600 border-red-200 hover:bg-red-50" data-testid="admin-openai-clear-btn">
                  Remover chave
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" size="sm" onClick={testar} disabled={testing || (!config?.configured && !apiKey.trim())} data-testid="admin-openai-test-btn">
                {testing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1" />}
                {testing ? 'A testar…' : 'Testar chave'}
              </Button>
              <Button type="button" size="sm" onClick={guardar} disabled={saving || (!apiKey.trim() && model === (config?.model || defaultModel))} data-testid="admin-openai-save-btn">
                {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
                {saving ? 'A guardar…' : 'Guardar'}
              </Button>
            </div>
          </div>
        </Card>

        <Card className="p-4 bg-stone-50 border-dashed">
          <h3 className="text-sm font-semibold text-stone-700 mb-1 flex items-center gap-1.5"><Sparkles className="h-4 w-4 text-purple-500" /> Onde é usada?</h3>
          <p className="text-xs text-stone-600 leading-relaxed">
            Quando esta chave está configurada, o painel <strong>Ranking Final</strong> ganha um botão
            <span className="mx-1 inline-flex items-center gap-1 text-purple-700 font-semibold"><Sparkles className="h-3 w-3" /> Sugerir com IA</span>
            ao lado da caixa &quot;Mensagem à equipa&quot;. O ChatGPT analisa a produtividade da(s) semana(s) seleccionada(s) — nível, tendência, peças acabadas, horas — e propõe uma mensagem motivadora que o gestor pode editar antes de guardar. Sem chave, o botão continua visível mas devolve um aviso.
          </p>
        </Card>
      </div>
    </GestorLayout>
  );
}
